import type { AxiosError, AxiosRequestConfig } from 'axios'

import Axios from 'axios'

import { authControllerRefreshUserToken } from '@/api/auth/auth'
import { redirectToSignIn } from '@/shared/lib/auth'
import { getAccessToken, setAccessToken } from '@/shared/lib/auth/token'

export const AxiosApiInstance = Axios.create()

AxiosApiInstance.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: AxiosError | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })

  failedQueue = []
}

AxiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined
    if (!originalRequest) return Promise.reject(error)

    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh-token')
    const isUserSelfRequest = originalRequest.url?.includes('/user/self/details')
    const isVerificationRequest = originalRequest.url?.includes('/sumsub/token')
    const isOnboardingRequest = originalRequest.url?.includes('/onboarding/')
    const isProtectedRequest = isUserSelfRequest || isVerificationRequest

    // Onboarding uses its own cookie session, so it must not trigger the app's refresh/sign-in flow
    if (isOnboardingRequest) return Promise.reject(error)

    if (error.response?.status === 401) {
      if (isRefreshRequest) {
        processQueue(error)
        isRefreshing = false
        redirectToSignIn()
        return Promise.reject(error)
      }

      if (isProtectedRequest && !originalRequest._retry) {
        return Promise.reject(error)
      }

      if (!originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => AxiosApiInstance(originalRequest))
            .catch((err) => Promise.reject(err))
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const { accessToken } = await authControllerRefreshUserToken()
          setAccessToken(accessToken)

          processQueue(null)
          isRefreshing = false

          return AxiosApiInstance(originalRequest)
        } catch (refreshError) {
          processQueue(refreshError as AxiosError)
          isRefreshing = false
          redirectToSignIn()
          return Promise.reject(error)
        }
      }
    }

    return Promise.reject(error)
  },
)

export const axiosClient = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
  const source = Axios.CancelToken.source()
  const promise = AxiosApiInstance({
    ...config,
    ...options,
    cancelToken: source.token,
    withCredentials: true,
    paramsSerializer: {
      indexes: null,
    },
  }).then(({ data }) => data)

  // @ts-expect-error Cancel is not typed correctly in axios
  promise.cancel = () => {
    source.cancel('Query was cancelled')
  }

  return promise
}

export type ErrorType<Error> = AxiosError<Error>

export const getErrorMessage = (error: unknown) => {
  const err = error as ErrorType<{ message: string }>
  if (!err.response) return err.message

  return err.response.data.message
}
